/**
 * The Product & Engineering Overview (WALLIYO-0132), built to the approved
 * design.
 *
 * Worst first means by kind, not by age: an open incident is in front of
 * customers, a rule taking effect on Monday is not yet anybody's problem, and an
 * experiment far enough in is a decision waiting rather than a fault.
 *
 * Unconfirmed callbacks get a panel of their own because every one of them is a
 * pro who paid and is not marked paid — not a statistic to whoever it happened
 * to.
 */

import type { DefinedRuleId } from "@wallyo/contracts/rules";

import type { ProductOverview as Data } from "@wallyo/contracts/console/overview";

import { ruleName, t, tCount } from "../../i18n";
import { formatNumber } from "../../i18n/format";
import { Figures } from "./OverviewScreen";
import { QueueRows, Tallies } from "./QueueRows";

/**
 * A scheduled rule arrives as its id, because the server names nothing.
 *
 * Its words live here, the same ones the Rules screen shows, so the two screens
 * never disagree about what a rule is called.
 */
const named = (rows: Data["broken"]): Data["broken"] =>
  rows.map((row) =>
    row.rowId === "ruleScheduled" ? { ...row, subject: ruleName(row.subject as DefinedRuleId) } : row,
  );

export function ProductOverviewView({ data }: { data: Data }) {
  return (
    <>
      <Figures figures={data.figures} />

      <div className="ov-split">
        <section className="card">
          <div className="ov-card-head">
            <h2>{t("overview.product.whatIsBroken")}</h2>
            <span className="foot">{t("overview.product.worstFirst")}</span>
          </div>
          <QueueRows rows={named(data.broken)} area="product" empty={t("overview.product.nothingBroken")} />
        </section>

        <div className="ov-side">
          {data.unconfirmed ? (
            <section className="card ov-callout bad">
              <h2>{data.unconfirmed.provider}</h2>
              <p className="foot">
                {tCount("overview.product.unconfirmed", data.unconfirmed.count, {
                  count: formatNumber(data.unconfirmed.count),
                  minutes: formatNumber(data.unconfirmed.sinceMinutes),
                })}
              </p>
              <p className="foot danger">{t("overview.product.unconfirmedWhy")}</p>
              <a className="go-link" href="#payments">
                {t("overview.product.replayThem")}
              </a>
            </section>
          ) : null}

          <section className="card">
            <h2>{t("overview.product.rolledOut")}</h2>
            <Tallies tallies={data.rollout} area="product" />
          </section>
        </div>
      </div>
    </>
  );
}
